import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { InstanceProvider } from './hooks/useInstance';
import { LoginForm } from './components/LoginForm';
import { SetPasswordForm } from './components/SetPasswordForm';
import { WaitlistForm } from './components/WaitlistForm';
import { Layout } from './components/Layout';
import { Dashboard } from './components/Dashboard';
import { VocabTree } from './components/VocabTree';
import { CollectionsPage } from './components/CollectionsPage';
import { AdminRoute } from './components/AdminRoute';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { NotFound } from './pages/NotFound';
import { HelpPage } from './pages/HelpPage';
import { SettingsPage } from './pages/SettingsPage';
import { ShareTargetPage } from './pages/ShareTargetPage';
import { HardPaywall } from './components/HardPaywall';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { Loader2 } from 'lucide-react';

const AppContent: React.FC = () => {
  const { token, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <Routes>
        <Route path="/signup" element={<WaitlistForm />} />
        <Route path="/waitlist" element={<Navigate to="/signup" replace />} />
        <Route path="/set-password" element={<SetPasswordForm />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/" element={<LoginForm />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  const isPaymentProcessing =
    !user?.is_active && window.location.search.includes('subscription=success');

  if (user && user.is_active === false) {
    return <HardPaywall isPaymentProcessing={isPaymentProcessing} />;
  }

  return (
    <InstanceProvider>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="tree" element={<VocabTree />} />
          <Route path="collections" element={<CollectionsPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="share" element={<ShareTargetPage />} />
          <Route
            path="admin/users"
            element={<AdminRoute><AdminUsersPage /></AdminRoute>}
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </InstanceProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;

